import Box from '@mui/material/Box';
import type { CSSObject } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { intent, radius, sizes, spacingTokens, state, stateAlpha, text, typography } from '../../theme/tokens';

/**
 * The always-visible **label tab** shared by every DS text-entry field — the brand chrome that
 * sits above the input: a rounded, filled tab carrying the label (+ a required `*`) and, under
 * it, a `bd4` strip that fills the gap down to the input's top corner arcs (Figma "label" frame,
 * node 801:1659).
 *
 * Two components host it — {@link TextField} (plain MUI `TextField`) and `PickerField`
 * (MUI X `PickersTextField`, which the date/time pickers require for accessible section editing).
 * They can't share a base component, but they DO share this chrome, so it lives here once
 * instead of being kept in sync by hand in two files.
 *
 * ── How the states are split ────────────────────────────────────────────────────────────────
 * `readOnly` / `disabled` / `error` / `success` are known at render time, so they arrive as the
 * `fieldState` prop. **`hover` and `focus` are pure CSS** — they used to be React state, which
 * re-rendered the whole field on every mouse enter. The resulting priority chain is unchanged:
 * readOnly > disabled > error > success > focused > hover > default.
 *
 * Focus keys off `:has(.Mui-focused)`, NOT `:focus-within`: the tab and the input's outline must
 * light up together, and MUI owns that decision via `.Mui-focused`. The two diverge in real
 * cases — after picking a date the picker returns focus to the calendar BUTTON in the end
 * adornment, which is inside this root (so `:focus-within` is true) while the field is no longer
 * focused (so the outline has already gone back to default). Keying off MUI's own class also
 * covers the opposite case for free: while a picker's popover is open, focus sits in the portal
 * OUTSIDE this root, yet MUI keeps `.Mui-focused` on the input — so the tab stays purple with no
 * extra prop needed.
 */
export type FieldState = 'default' | 'readOnly' | 'disabled' | 'error' | 'success';

/**
 * The 4% black hover overlay applied by DARKENING a solid fill rather than layering over it —
 * used where the tab already carries a solid intent colour (error / success), which a
 * translucent black layer would turn muddy. `stateAlpha.hover` is the single source of the 4%.
 */
const hoverDarken = (color: string) => `color-mix(in srgb, ${color}, black ${Math.round(stateAlpha.hover * 100)}%)`;

/** The same 4% overlay LAYERED over a fill, for the neutral (default) tab. */
const hoverOverlay = `linear-gradient(${state.hover}, ${state.hover})`;

const TAB = '& .OxField-labelTab';
const STRIP = '& .OxField-labelStrip';

/** Tab fill + label colour, and the strip colour that continues it, for one resting state. */
const resting = (fill: string | undefined, color: string): { tab: CSSObject; strip: CSSObject } => ({
  tab: { ...(fill ? { backgroundColor: fill } : {}), color },
  strip: fill ? { backgroundColor: fill } : {},
});

// Resting appearance per state. The strip's colour tracks the TAB's OWN FILL (`intent.*.solid`),
// never `intent.*.border`: in Figma the strip lives inside the `label` frame, so tab and strip
// are one solid block — the soft `*040` tint belongs to the input box's outline (fixed
// 2026-08-12). `readOnly` has no fill at all, so it has no strip either.
const RESTING: Record<FieldState, { tab: CSSObject; strip: CSSObject }> = {
  readOnly: resting(undefined, text.secondary),
  disabled: resting(intent.neutral.subtle, text.disabled),
  error: resting(intent.error.solid, intent.error.onSolid),
  success: resting(intent.success.solid, intent.success.onSolid),
  default: resting(intent.neutral.subtle, intent.neutral.onSolid),
};

// Hover. Only three states react to it: error / success darken their solid fill, and the neutral
// default tab takes the translucent overlay AND deepens its strip from `subtle` to `border`.
// disabled / readOnly deliberately have none (they used to be unreachable branches in the old
// if/else chain, since the mouse handlers were skipped for them).
const HOVER: Partial<Record<FieldState, { tab: CSSObject; strip?: CSSObject }>> = {
  error: { tab: { backgroundColor: hoverDarken(intent.error.solid) } },
  success: { tab: { backgroundColor: hoverDarken(intent.success.solid) } },
  default: {
    tab: { backgroundImage: hoverOverlay },
    strip: { backgroundColor: intent.neutral.border },
  },
};

/**
 * Focus is whatever MUI marks as focused — the same signal that drives the input's outline, so
 * the tab and the outline can never disagree. See the note in the module docstring for the two
 * cases where this differs from `:focus-within` (adornment button keeps focus after a date is
 * picked; popover holds focus outside the root while open).
 */
export const FOCUS_SELECTOR = '&:has(.Mui-focused)';

// Focus — the default state only; error / success / disabled / readOnly outrank it, exactly as
// they did in the old priority chain. `backgroundImage: none` clears a hover overlay underneath.
const FOCUS: { tab: CSSObject; strip: CSSObject } = {
  tab: {
    backgroundColor: intent.primary.solid,
    backgroundImage: 'none',
    color: intent.primary.onSolid,
  },
  strip: { backgroundColor: intent.primary.solid },
};

/**
 * Layout root for a field: a column holding the label tab, the input, and the helper text.
 * It is also the **state host** — `:hover` / `:focus-within` land here and cascade down to the
 * tab and strip, which is why the tab can't be a standalone styled component.
 *
 * A consumer's `sx` still wins: MUI injects `sx` after `styled()` overrides.
 */
export const StyledFieldRoot = styled(Box, {
  shouldForwardProp: prop => prop !== 'fieldState' && prop !== 'fullWidth',
})<{ fieldState: FieldState; fullWidth?: boolean }>(({ fieldState, fullWidth }) => {
  const restingState = RESTING[fieldState];
  const hover = HOVER[fieldState];

  return {
    display: fullWidth ? 'flex' : 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    ...(fullWidth && { width: '100%' }),

    // Tab geometry + its resting colours, merged into ONE key. A second `[TAB]` key in this
    // object literal would REPLACE this rule rather than extend it (the same trap documented
    // on `.SelectionChip-circle`), so every state is merged in here instead.
    //
    // zIndex puts the tab above the input's fieldset border (MUI extends it above the root to
    // accommodate the floating-label notch — we keep that default and sit on top of it).
    // boxSizing + paddingTop keep the total height at h18 while giving the text sp2 of air.
    [TAB]: {
      display: 'inline-flex',
      alignItems: 'flex-end',
      gap: spacingTokens.sp2,
      height: sizes.height.h18,
      boxSizing: 'border-box',
      paddingTop: spacingTokens.sp2,
      position: 'relative',
      zIndex: 1,
      ...(fieldState !== 'readOnly' && {
        paddingLeft: spacingTokens.sp12,
        paddingRight: spacingTokens.sp12,
        borderRadius: `${radius.sm}px ${radius.sm}px 0 0`,
      }),
      ...restingState.tab,
    },
    [STRIP]: { display: 'block', height: sizes.border.bd4, ...restingState.strip },

    // Source order matters: focus is written after hover, so at equal specificity focus wins.
    ...(hover && {
      '&:hover': {
        [TAB]: hover.tab,
        ...(hover.strip ? { [STRIP]: hover.strip } : {}),
      },
    }),
    ...(fieldState === 'default' && {
      [FOCUS_SELECTOR]: { [TAB]: FOCUS.tab, [STRIP]: FOCUS.strip },
    }),
  };
});

const StyledLabelText = styled('span')({
  fontFamily: 'inherit',
  fontSize: typography.caption.fontSize,
  lineHeight: typography.caption.lineHeight,
  color: 'inherit',
});

const StyledRequiredMark = styled('span')({
  fontFamily: 'inherit',
  fontWeight: typography.captionBold.fontWeight,
  fontSize: typography.captionBold.fontSize,
  lineHeight: typography.captionBold.lineHeight,
  color: intent.error.text,
});

export interface FieldLabelTabProps {
  /** Label content. */
  label: ReactNode;
  /** Id put on the tab so the input can point at it with `aria-labelledby`. */
  labelId: string;
  /** Appends the red `*`. */
  required?: boolean;
  /** Read-only fields drop the fill, the side padding and the strip entirely. */
  readOnly?: boolean;
}

/**
 * The tab markup. Colours come from {@link StyledFieldRoot} — this renders structure only, so it must
 * be nested inside a `FieldRoot` to be styled at all.
 *
 * `alignItems: stretch` on the wrapper makes the strip match the tab's width without anyone
 * having to know it in advance; the negative bottom margin overlaps the input so the strip sits
 * at the level of its top corner arcs. `pointerEvents: none` keeps the tab out of the way of
 * clicks aimed at the input.
 */
export function FieldLabelTab({ label, labelId, required, readOnly }: FieldLabelTabProps) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        ...(readOnly ? {} : { marginBottom: `-${sizes.border.bd4}px` }),
      }}
    >
      <span className="OxField-labelTab" id={labelId}>
        <StyledLabelText>{label}</StyledLabelText>
        {required && <StyledRequiredMark>*</StyledRequiredMark>}
      </span>
      {/* No strip on a read-only field — there is no tab fill for it to continue. */}
      {!readOnly && <span className="OxField-labelStrip" />}
    </Box>
  );
}

/** Resolves the render-time state props into the single `fieldState` the chrome is driven by. */
export function resolveFieldState({
  readOnly,
  disabled,
  error,
  success,
}: {
  readOnly?: boolean;
  disabled?: boolean;
  error?: boolean;
  success?: boolean;
}): FieldState {
  if (readOnly) return 'readOnly';
  if (disabled) return 'disabled';
  if (error) return 'error';
  if (success) return 'success';
  return 'default';
}
